import { useEffect, useState, type ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ActionMenu,
  Card,
  CardBody,
  Input,
  Pagination,
  Select,
  StatusPill,
  Switch,
  TableScroll,
  useCursorPagination,
} from "@playbookai/ui";
import { Search } from "lucide-react";
import { useAuthStore } from "../../auth/auth-store.js";
import { useDateTimeFormatter } from "../../settings/format-date-time.js";
import { AdminTimeZoneLabel } from "./AdminTimeZoneLabel.js";
import {
  listIdentityActivity,
  type IdentityActivitySource,
  type IdentityActivityStatus,
} from "../../api/identity-admin-api.js";
import { useProjectionRetry } from "./useProjectionRetry.js";

const PAGE_SIZE = 25;

export function IdentityActivityTab(): ReactElement {
  const formatter = useDateTimeFormatter();
  const { dateTimeOr } = formatter;
  const tenantId = useAuthStore((state) => state.claims?.tenantId ?? "none");
  const [source, setSource] = useState<IdentityActivitySource | "all">("all");
  const [status, setStatus] = useState<IdentityActivityStatus | "all">("all");
  const [windowDays, setWindowDays] = useState("30");
  const [failuresOnly, setFailuresOnly] = useState(false);
  const [searchState, setSearchState] = useState({ tenantId, text: "" });
  const [debouncedSearchState, setDebouncedSearchState] = useState({
    tenantId,
    text: "",
  });
  const search = searchState.tenantId === tenantId ? searchState.text : "";
  const debouncedSearch =
    debouncedSearchState.tenantId === tenantId ? debouncedSearchState.text : "";
  useEffect(() => {
    const timer = window.setTimeout(
      () => setDebouncedSearchState({ tenantId, text: search.trim() }),
      300,
    );
    return () => window.clearTimeout(timer);
  }, [search, tenantId]);
  const from = new Date(
    Date.now() - Number(windowDays) * 86_400_000,
  ).toISOString();
  const key = `${tenantId}|${source}|${status}|${windowDays}|${failuresOnly}|${debouncedSearch.toLocaleLowerCase()}`;
  const paging = useCursorPagination(key, { pageSize: PAGE_SIZE });
  const activity = useQuery({
    queryKey: ["identity-activity", key, paging.cursor ?? "first"],
    queryFn: ({ signal }) => {
      const selectedSource = source === "all" ? undefined : source;
      const selectedStatus = status === "all" ? undefined : status;
      return listIdentityActivity(
        {
          ...(selectedSource ? { source: selectedSource } : {}),
          ...(selectedStatus ? { status: selectedStatus } : {}),
          failuresOnly,
          search: debouncedSearch,
          from,
          ...(paging.cursor ? { cursor: paging.cursor } : {}),
          limit: PAGE_SIZE,
        },
        signal,
      );
    },
  });
  const retry = useProjectionRetry();
  const rows = activity.data?.items ?? [];
  /* Whether to carry an Actions column AT ALL, not just whether to draw a
     trigger in it. Suppressing only the per-row kebab left every read-only page
     with a headed column and nothing under it, which reads as "actions exist
     here and you may not use them" rather than "this is a record". Almost every
     activity row is read-only, and a page of them is the common case — only
     retryable projection failures carry an action — so the empty column was
     what most users saw. Derived from the rows actually on screen, so the
     column appears on the page that needs it and nowhere else. */
  const hasActions = rows.some((row) => row.action !== null);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-pb-text">
          Identity activity
        </h2>
        <p className="mt-1 text-sm text-pb-text-secondary">
          A focused operational history for directory, provisioning and access
          events.
        </p>
      </div>
      <Card>
        <CardBody flush>
          {/* One row from the page's own width (PB-5436), at the row's own
              minimum: 680px of fixed tracks, the Failures-only switch, four
              gaps, padding and border (~56rem). Below that it stacks rather
              than overflowing the card. */}
          <div className="grid gap-3 border-b border-pb-border p-4 @min-[56rem]/settings-page:grid-cols-[minmax(220px,1fr)_170px_150px_140px_auto] @min-[56rem]/settings-page:items-end">
            <Input
              aria-label="Search identity activity"
              placeholder="Search activity"
              value={search}
              prefix={<Search className="h-4 w-4" aria-hidden />}
              onChange={(event) =>
                setSearchState({ tenantId, text: event.target.value })
              }
            />
            <Select
              label="Source"
              value={source}
              options={[
                { value: "all", label: "All sources" },
                { value: "directory", label: "Directory" },
                { value: "provisioning", label: "Provisioning" },
                { value: "access", label: "Access" },
                { value: "teams", label: "Teams" },
              ]}
              onChange={(value) =>
                setSource(value as IdentityActivitySource | "all")
              }
            />
            <Select
              label="Status"
              value={status}
              options={[
                { value: "all", label: "All statuses" },
                { value: "success", label: "Succeeded" },
                { value: "failure", label: "Failed" },
              ]}
              onChange={(value) =>
                setStatus(value as IdentityActivityStatus | "all")
              }
            />
            <Select
              label="Date range"
              value={windowDays}
              options={[
                { value: "7", label: "Last 7 days" },
                { value: "30", label: "Last 30 days" },
                { value: "90", label: "Last 90 days" },
              ]}
              onChange={setWindowDays}
            />
            <Switch
              checked={failuresOnly}
              onChange={setFailuresOnly}
              label="Failures only"
            />
          </div>
          {activity.isPending ? (
            <p role="status" className="p-6 text-sm text-pb-text-muted">
              Loading activity…
            </p>
          ) : null}
          {activity.error ? (
            <p role="alert" className="p-6 text-sm text-pb-red-text">
              {activity.error.message}
            </p>
          ) : null}
          {activity.isSuccess && rows.length === 0 ? (
            /* THREE different facts, and telling a reader the wrong one sends
               them to adjust a filter that was never the problem.

               THE CURSOR IS READ FIRST, ahead of the page number. The server
               bounds how far it reads in one request, so ANY page — the first
               one included — can come back empty while the scan is still
               offering to look further. Saying the filters match nothing there
               is the same false statement as the defect being fixed, only in
               the opposite direction: it blames the reader's filters for a
               search that has not finished (PB-5402 review, second pass).

               Once the server has stopped offering a next page the answer is
               settled, and only then does the page number decide which settled
               fact it is: on the first page an empty result really is the
               filters, and past it the reader has paged beyond what this view
               can show — a position they undo with Previous, not a filter they
               need to change. */
            <p className="p-6 text-sm text-pb-text-muted">
              {activity.data?.nextCursor
                ? "No activity on this page. Use Next page to keep looking further back."
                : paging.page === 1
                  ? "No activity matches these filters."
                  : "No further activity after the previous page."}
            </p>
          ) : null}
          {rows.length > 0 ? (
            <TableScroll aria-label="Identity activity" embedded>
              {/* eslint-disable-next-line ds/no-hand-built -- DataTable would restyle this header (uppercase 10px labels) and cell padding; no PR 3 look row approves that, so the raw table stays inside TableScroll. */}
              <table className="w-full text-dense-1">
                <thead className="bg-pb-card-elevated text-left">
                  <tr className="border-b border-pb-border">
                    <th className="px-4 py-3 font-medium">Activity</th>
                    <th className="px-4 py-3 font-medium">Source</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">
                      <AdminTimeZoneLabel label="When" formatter={formatter} />
                    </th>
                    {hasActions ? (
                      <th className="px-4 py-3 text-right font-medium">
                        Actions
                      </th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={row.id}
                      className="border-b border-pb-border last:border-0"
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-pb-text">
                          {row.title}
                        </div>
                        <div className="mt-0.5 text-pb-text-muted">
                          {[row.detail, row.actorName]
                            .filter(Boolean)
                            .join(" · ") || "System event"}
                        </div>
                        {row.action && retry.errorFor(row.action.syncId) ? (
                          <div role="alert" className="mt-1 text-pb-red-text">
                            {retry.errorFor(row.action.syncId)}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 capitalize text-pb-text-secondary">
                        {row.source}
                      </td>
                      <td className="px-4 py-3">
                        <StatusPill
                          tone={
                            row.status === "failure" ? "critical" : "healthy"
                          }
                        >
                          {row.status === "failure" ? "Failed" : "Succeeded"}
                        </StatusPill>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-pb-text-secondary">
                        {dateTimeOr(row.occurredAt, "—")}
                      </td>
                      {/* The cell is bound to the same `hasActions` as the header
                          above, never to this row's own action — a row without
                          one still needs its empty cell to keep the column
                          aligned when a SIBLING row has an action. Dropping the
                          whole column is what happens when no row does. */}
                      {hasActions ? (
                        <td className="px-4 py-3 text-right">
                          {/* NO trigger when the row has nothing to do. Almost every
                            activity row is a read-only record, so rendering the
                            kebab regardless put a control on each one whose only
                            content was a disabled "No actions available" — an
                            invitation to click that answers nothing. `reason` is
                            for an action that EXISTS and is currently unavailable;
                            it is not a way to describe the absence of any action,
                            and nowhere else in the console does that. Only
                            retryable projection failures carry one. */}
                          {row.action ? (
                            <ActionMenu
                              triggerSize="sm"
                              triggerLabel={`Actions for ${row.title}`}
                              items={[
                                {
                                  id: "retry",
                                  label: retry.isRetrying(row.action.syncId)
                                    ? "Retrying…"
                                    : "Retry projection",
                                  ...(retry.isRetrying(row.action.syncId)
                                    ? {
                                        disabledReason:
                                          "A retry is already in progress.",
                                      }
                                    : {}),
                                  onSelect: () =>
                                    retry.retry(row.action!.syncId),
                                },
                              ]}
                            />
                          ) : null}
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          ) : null}
          {paging.page > 1 || activity.data?.nextCursor ? (
            <div className="flex justify-end border-t border-pb-border p-3">
              <Pagination
                mode="cursor"
                page={paging.page}
                pageItemCount={rows.length}
                canGoBack={paging.canGoBack}
                hasNext={Boolean(activity.data?.nextCursor)}
                busy={activity.isFetching}
                onPrev={paging.goBack}
                onNext={() => {
                  const cursor = activity.data?.nextCursor;
                  if (cursor) paging.goNext(cursor);
                }}
              />
            </div>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}
